import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bug, User, UserPlus, ArrowRight } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { useNavigate, useLocation } from "react-router-dom";

const TestModeBar: React.FC = () => {
  const { state, dispatch } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Only show when test mode is enabled
  if (!state.settings.testMode) {
    return null;
  }

  const handleMockSignIn = () => {
    console.log('Mock Sign In triggered');
    
    // Simulate successful sign-in
    // Only set test email if no email is currently stored
    if (!state.userEmail) {
      dispatch({ type: 'SET_EMAIL', payload: 'test.user@example.com' });
    }
    dispatch({ type: 'SET_MEMBER', payload: true });
    dispatch({ type: 'SET_GUEST', payload: false });
    dispatch({ type: 'SET_IS_SIGNUP', payload: false });
    
    // Navigate directly to reward method selection (skip member welcome)
    dispatch({ type: 'SET_STEP', payload: 8 });
    navigate("/reward-method-selection");
  };

  const handleMockSignUp = () => {
    console.log('Mock Sign Up triggered');
    
    // Simulate successful sign-up and membership selection
    // Only set test email if no email is currently stored
    if (!state.userEmail) {
      dispatch({ type: 'SET_EMAIL', payload: 'new.user@example.com' });
    }
    dispatch({ type: 'SET_MEMBER', payload: true });
    dispatch({ type: 'SET_GUEST', payload: false });
    dispatch({ type: 'SET_IS_SIGNUP', payload: true });
    
    // Navigate directly to reward method selection (skip membership selection)
    dispatch({ type: 'SET_STEP', payload: 8 });
    navigate("/reward-method-selection");
  };

  const handleNextStep = () => {
    console.log('Next Step triggered from:', location.pathname);
    
    if (location.pathname === '/add-empty') {
      // Navigate to checking empty (simulate item detection)
      dispatch({ type: 'SET_STEP', payload: 4 });
      navigate('/checking-empty');
    } else if (location.pathname === '/checking-empty') {
      // Navigate to empty accepted (simulate item acceptance)
      dispatch({ type: 'SET_STEP', payload: 5 });
      navigate('/empty-accepted');
    } else if (location.pathname === '/place-empty') {
      // Simulate item dispensed (change state, no navigation)
      dispatch({ type: 'SET_ITEM_DISPENSED', payload: true });
    } else if (location.pathname === '/remove-empty') {
      // Simulate item removed (change state, no navigation)
      dispatch({ type: 'SET_ITEM_DISPENSED', payload: true });
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="absolute top-0 left-0 right-0 z-50 bg-red-600 text-white shadow-lg border-b-2 border-red-700"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
      >
        <div className="flex items-center justify-between px-6 py-3">
          {/* Test Mode Indicator */}
          <div className="flex items-center gap-2">
            <Bug size={20} />
            <span className="font-sans font-semibold text-[14px] uppercase tracking-wide">
              Test Mode Active
            </span>
          </div>

          {/* Mock Auth Buttons */}
          <div className="flex items-center gap-3">
            {location.pathname === '/member-welcome' && (
              <>
                <button
                  onClick={handleMockSignIn}
                  className="flex items-center gap-2 bg-red-700 hover:bg-red-800 px-4 py-2 rounded-lg font-sans font-medium text-[14px] transition-colors duration-200"
                >
                  <User size={16} />
                  Mock Sign In
                </button>
                
                <button
                  onClick={handleMockSignUp}
                  className="flex items-center gap-2 bg-red-700 hover:bg-red-800 px-4 py-2 rounded-lg font-sans font-medium text-[14px] transition-colors duration-200"
                >
                  <UserPlus size={16} />
                  Mock Sign Up
                </button>
              </>
            )}

            {/* Context-specific buttons */}
            {location.pathname === '/add-empty' && (
              <button
                onClick={handleNextStep}
                className="flex items-center gap-2 bg-green-700 hover:bg-green-800 px-4 py-2 rounded-lg font-sans font-medium text-[14px] transition-colors duration-200"
              >
                <ArrowRight size={16} />
                Simulate Item Detection
              </button>
            )}

            {location.pathname === '/checking-empty' && (
              <button
                onClick={handleNextStep}
                className="flex items-center gap-2 bg-green-700 hover:bg-green-800 px-4 py-2 rounded-lg font-sans font-medium text-[14px] transition-colors duration-200"
              >
                <ArrowRight size={16} />
                Simulate Item Accepted
              </button>
            )}

            {location.pathname === '/place-empty' && !state.kiosk.isItemDispensed && (
              <button
                onClick={handleNextStep}
                className="flex items-center gap-2 bg-green-700 hover:bg-green-800 px-4 py-2 rounded-lg font-sans font-medium text-[14px] transition-colors duration-200"
              >
                <ArrowRight size={16} />
                Simulate Item Dispensed
              </button>
            )}

            {location.pathname === '/remove-empty' && !state.kiosk.isItemDispensed && (
              <button
                onClick={handleNextStep}
                className="flex items-center gap-2 bg-green-700 hover:bg-green-800 px-4 py-2 rounded-lg font-sans font-medium text-[14px] transition-colors duration-200"
              >
                <ArrowRight size={16} />
                Simulate Item Removed
              </button>
            )}
          </div>

          {/* Debug Info */}
          <div className="text-[12px] opacity-75">
            Step: {state.currentStep} | Items: {state.itemsCount} | Dispensed: {state.kiosk.isItemDispensed.toString()}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TestModeBar;
